import type { ListGangsResponse } from '../api/gangsApi';

// Participant payment type
export type PaymentType = 'percentage' | 'fixedRate';

// Participant information stored in the lifecycle form
export interface GangParticipant {
  userId: string;
  fullName: string;
  email: string;
  canSeeSplit: boolean;
  paymentType: PaymentType;
  defaultSplitPercentage: number | null;
  defaultRate: number | null;
  defaultUnitsValue: number | null;
}

export interface GangParticipantWithRole extends GangParticipant {
  role: 'leader' | 'member';
}

// Lifecycle form data shape used for both create and edit flows
export interface GangCreationFormData {
  name: string;
  isEqualSplit: boolean;
  participants: Record<string, GangParticipant>;
  leaderIds: string[];
  memberIds: string[];
}

// Gang creation steps
export enum GangCreationStep {
  NAME = 0,
  LEADERS = 1,
  MEMBERS = 2,
  MEMBER_CONFIG = 3,
  SUMMARY = 4,
}

// Validation schemas
export const GANG_CREATION_STEPS = [
  { label: 'Gang Name', description: 'Choose a unique name for your gang' },
  {
    label: 'Select Leaders',
    description: 'Choose privileged users who will lead the gang',
  },
  {
    label: 'Select Members',
    description: 'Choose users to add as gang members',
  },
  {
    label: 'Configure Members',
    description: 'Set permissions and rates for each member',
  },
  { label: 'Summary', description: 'Review and create your gang' },
];

type GangMemberResponse = ListGangsResponse['gangs'][number]['members'][number];

export const gangParticipantFromGangMember = (
  member: GangMemberResponse
): GangParticipant => {
  const given = (member.givenName ?? '').trim();
  const family = (member.familyName ?? '').trim();
  const composedName = `${given} ${family}`.trim();
  const fullName = composedName || given || family || 'Unknown';

  // Determine payment type based on which field is populated
  const hasRate = member.defaultRate != null;
  const paymentType: PaymentType = hasRate ? 'fixedRate' : 'percentage';

  return {
    userId: member.userId,
    fullName,
    email: (member.email ?? '').trim(),
    canSeeSplit: Boolean(member.canSeeSplit),
    paymentType,
    defaultSplitPercentage:
      member.defaultSplitPercentage === null ||
      member.defaultSplitPercentage === undefined
        ? null
        : member.defaultSplitPercentage * 100,
    defaultRate:
      member.defaultRate === null || member.defaultRate === undefined
        ? null
        : member.defaultRate,
    defaultUnitsValue:
      member.defaultUnitsValue === null ||
      member.defaultUnitsValue === undefined
        ? null
        : member.defaultUnitsValue,
  };
};
